import React, {
  useCallback,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/system'
import { generateManifestPDF } from './ShippingManifestPDF'
import { useAppAlertsActionsContext } from '../../AppAlerts/AppAlertsProvider'
import { SHIPPING_STATUS_CODES } from '../../../common/constants'
import {
  SHIPPINGS_TABLE_HEADER_ORDER,
  SHIPPINGS_TABLE_HEAD_CELLS,
} from '../../../common/constants'
import SplitButton from '../../../ui/components/splitButton'
import PrintModal from '../../../ui/modals/print'

import ShippingsOverview from '../../ShippingsOverview'

import CancelShippingModal from '../CancelShippingModal'
import ActivateReturnDrawer from '../ActivateReturnDrawer'
import useShippingActions from './useShippingActions'
import useTranslations from '../../../i18n/useTranslations'
import Loader from '../../../ui/views/Loader'
import ScheduledPickupDrawer from '../../../ui/modules/ScheduledPickupDrawer'
import ModalLayout from '../../../ui/modals/layout'
import { Button } from '@mui/material'

const useSelectableShippings = (shippings, states) => {
  const [selected, _setSelected] = useState([])

  const toggleSelection = useCallback(
    (shippingInfo, state) => {
      if (!!selected.find(x => x.shippingCode === shippingInfo.shippingCode)) {
        _setSelected(selectedShippings => [
          ...selectedShippings.filter(
            selectedShipping =>
              selectedShipping.shippingCode !== shippingInfo.shippingCode,
          ),
        ])
      } else {
        _setSelected(selectedShippings => [
          ...selectedShippings,
          { ...shippingInfo, state: state },
        ])
      }
    },
    [selected, states],
  )

  const toggleAll = useCallback(
    isChecked => {
      if (isChecked) {
        const nextSelected = shippings.map(({ info }) => {
          const state = states.find(s => s.shippingCode === info.shippingCode)
          return { ...info, state: state ? state : null }
        })
        const difference = selected.filter(
          shipment =>
            !nextSelected.some(
              ({ shippingCode }) => shippingCode === shipment.shippingCode,
            ),
        )
        _setSelected([...difference])
      } else {
        const nextSelected = shippings.map(({ info }) => {
          const state = states.find(s => s.shippingCode === info.shippingCode)
          return { ...info, state: state ? state : null }
        })
        const difference = nextSelected.filter(shipment =>
          selected.filter(
            ({ shippingCode }) => shippingCode !== shipment.shippingCode,
          ),
        )
        const remainingSelected = selected.filter(
          ({ shippingCode }) =>
            !nextSelected.some(
              nextItem => nextItem.shippingCode === shippingCode,
            ),
        )
        _setSelected([...remainingSelected, ...difference])
      }
    },
    [shippings, states, selected],
  )

  const clearSelection = useCallback(() => {
    _setSelected([])
  }, [])

  return { selected, toggleSelection, toggleAll, clearSelection }
}

const TrackingTable = forwardRef(
  (
    {
      isLoading,
      shippings,
      states,
      tableParams,
      showPagination,
      onChangeTableParams,
      collapsableHeader,
      shippingsType,
      onClickResolveIncident,
      changeSelectedTracking,
      setNumberSelected,
      numberSelected
    },
    ref,
  ) => {
    const {
      selected: selectedShippingsInfo,
      toggleSelection: toggleShippingSelection,
      toggleAll: toggleAllShippings,
      clearSelection: clearShippingSelection,
    } = useSelectableShippings(shippings, states)

    useEffect(() => {
      setNumberSelected(selectedShippingsInfo)
    }, [selectedShippingsInfo])

    useEffect(() => {
      if(!numberSelected)
        clearShippingSelection()
    }, [numberSelected])

    const {
      determineShippingActions,
      determineShippingsGroupOptions,
      shippingsInfoToCancel,
      confirmShippingsCancellation,
      abortShippingsCancellation,
      abortPrintProcess,
      confirmPrintShippings,
      shippingsToPrintAfterUserConfirm,
      activateReturnShippingInfo,
      cancelActivateReturn,
      scheduledPickupShippingsInfo,
      cancelScheduledPickup,
      showSuccessModal,
      setShowSuccessModal,
    } = useShippingActions()
    const shouldShowCancelShippingsModal = shippingsInfoToCancel.length > 0
    const shouldShowPrintSettingsModal =
      shippingsToPrintAfterUserConfirm.length > 0

    const t = useTranslations()
    const { setShowCancelGenerateManifestAlert } = useAppAlertsActionsContext()

    const onGenerateManifest = () => {
      const hasInvalidShippings =
        selectedShippingsInfo.length === 0 ||
        selectedShippingsInfo.some(shipping => {
          return (
            shipping?.state?.lastStatusCode !==
              SHIPPING_STATUS_CODES.manifested || shipping?.pickupUnscheduled
          )
        })
      if (hasInvalidShippings) {
        setShowCancelGenerateManifestAlert(true)
        return
      }
      generateManifestPDF(selectedShippingsInfo, t)
    }

    useImperativeHandle(ref, () => ({
      onGenerateManifest,
    }))

    const splitButtonOptions = useMemo(() => {
      const allOptions = determineShippingsGroupOptions(
        selectedShippingsInfo,
        clearShippingSelection,
        shippingsType,
      )

      return allOptions.filter(option => option.isActive)
    }, [
      clearShippingSelection,
      determineShippingsGroupOptions,
      selectedShippingsInfo,
      shippingsType,
    ])

    return !isLoading ? (
      <>
        <ShippingsOverview
          onCheckboxChange={toggleShippingSelection}
          onChangeTableParams={onChangeTableParams}
          tableParams={tableParams}
          headCellsOrder={SHIPPINGS_TABLE_HEADER_ORDER}
          headCells={SHIPPINGS_TABLE_HEAD_CELLS}
          states={states}
          rows={shippings}
          selectedRows={selectedShippingsInfo}
          getActionsForRow={determineShippingActions}
          onSelectAllClick={toggleAllShippings}
          onClickResolveIncident={onClickResolveIncident}
          showCheckbox
          showIcon
          showMenu
          showPagination={showPagination}
          collapsableHeader={collapsableHeader}
          shippingsType={shippingsType}
          changeSelectedTracking={changeSelectedTracking}
        />
        {selectedShippingsInfo.length > 0 ? (
          <>
            <Box sx={{ justifyContent: 'flex-end', display: 'flex', mb: 2 }}>
              <SplitButton size="medium" options={splitButtonOptions} />
            </Box>
          </>
        ) : null}
        {shouldShowCancelShippingsModal ? (
          <CancelShippingModal
            shippings={shippingsInfoToCancel}
            abortCancellation={abortShippingsCancellation}
            confirmCancellation={confirmShippingsCancellation}
          />
        ) : null}
        {shouldShowPrintSettingsModal ? (
          <PrintModal
            onCancel={abortPrintProcess}
            onConfirm={confirmPrintShippings}
            shippingsCount={
              // TODO: considerar pasar directamente `selectedShippingsCodes.length`
              // y que sea el archivo de traducciones el que calcule lo que se tiene
              // que mostrar
              selectedShippingsInfo.length === 0
                ? 1
                : selectedShippingsInfo.length
            }
          />
        ) : null}

        <ModalLayout
          isOpen={showSuccessModal}
          title={t('print-modal:title')}
          content={t('print-modal:body')}
          actions={
            <>
              <Button
                type="button"
                size="small"
                onClick={() => {
                  setShowSuccessModal(false)
                }}
              >
                {t('action:close')}
              </Button>
            </>
          }
        />

        <ActivateReturnDrawer
          activateReturnShippingInfo={activateReturnShippingInfo}
          onClickClose={cancelActivateReturn}
          onClickShowReturnsList={changeSelectedTracking}
        />

        <ScheduledPickupDrawer
          shippingsInfo={scheduledPickupShippingsInfo}
          onClickClose={cancelScheduledPickup}
          onClickConfirm={clearShippingSelection}
        />
      </>
    ) : (
      <Loader />
    )
  },
)
TrackingTable.propTypes = {
  isLoading: PropTypes.bool,
  states: PropTypes.array,
  shippings: PropTypes.array,
  tableParams: PropTypes.object,
  showPagination: PropTypes.bool,
  collapsableHeader: PropTypes.object,
  shippingsType: PropTypes.string,
  onChangeTableParams: PropTypes.func,
  onClickResolveIncident: PropTypes.func,
  changeSelectedTracking: PropTypes.func,
}

export default TrackingTable
